import { inject, Injectable, InjectionToken } from '@angular/core';

import { error, log } from '@jepto/shared/utils/logger';

/** Account-assets origin for oversized WS card payloads (e.g. `https://dev.jepto.com`). */
export const ANALYTICS_CARD_DATA_REF_ORIGIN = new InjectionToken<string>(
	'ANALYTICS_CARD_DATA_REF_ORIGIN',
	{
		providedIn: 'root',
		factory: () => '',
	}
);

export type CardDataRefFetchResult =
	| { ok: true; data: unknown }
	| { ok: false; error: Error };

const GZIP_MAGIC_0 = 0x1f;
const GZIP_MAGIC_1 = 0x8b;

/**
 * Builds `https://{host}/{dataRef}` for an account-relative gzip object path.
 * Rejects absolute URLs and path traversal.
 */
export function buildCardDataRefUrl(origin: string, dataRef: string): string {
	const base = origin.trim().replace(/\/+$/, '');
	const path = dataRef.trim().replace(/^\/+/, '');
	if (!base) {
		throw new Error('Card dataRef origin is empty');
	}
	if (
		!path.startsWith('account/') ||
		path.includes('..') ||
		path.includes('://')
	) {
		throw new Error(`Invalid card dataRef path: ${dataRef}`);
	}
	return `${base}/${path}`;
}

/** True when a WS envelope points at S3 instead of inline `data`. */
export function wsEventHasDataRef(
	event: unknown
): event is { dataRef: string } {
	if (!event || typeof event !== 'object' || !('dataRef' in event)) {
		return false;
	}
	const ref = (event as { dataRef?: unknown }).dataRef;
	return typeof ref === 'string' && ref.trim().length > 0;
}

export function shouldResolveCardDataRef(event: {
	cardType?: string;
	dataRef?: string;
}): boolean {
	if (typeof event.dataRef !== 'string' || !event.dataRef.trim()) {
		return false;
	}
	return (
		event.cardType === 'chart' ||
		event.cardType === 'table' ||
		event.cardType === 'analysis'
	);
}

function isGzipBuffer(bytes: Uint8Array): boolean {
	return (
		bytes.length >= 2 &&
		bytes[0] === GZIP_MAGIC_0 &&
		bytes[1] === GZIP_MAGIC_1
	);
}

function copyBytes(source: Uint8Array): ArrayBuffer {
	const buffer = new ArrayBuffer(source.byteLength);
	const view = new Uint8Array(buffer);
	for (let i = 0; i < source.byteLength; i++) {
		view[i] = source[i];
	}
	return buffer;
}

async function inflateGzip(bytes: Uint8Array): Promise<Uint8Array> {
	if (typeof DecompressionStream === 'undefined') {
		throw new Error('gzip decompression is not supported in this browser');
	}
	const source = new ReadableStream({
		start(controller) {
			(
				controller as unknown as { enqueue: (chunk: unknown) => void }
			).enqueue(bytes);
			controller.close();
		},
	});
	const pipeThrough = source.pipeThrough.bind(source) as (
		transform: unknown
	) => ReadableStream;
	const reader = pipeThrough(new DecompressionStream('gzip')).getReader();
	const chunks: ArrayBuffer[] = [];
	let total = 0;
	for (;;) {
		const { done, value } = await reader.read();
		if (done) {
			break;
		}
		if (ArrayBuffer.isView(value)) {
			const indexed = value as unknown as {
				byteLength: number;
				[index: number]: number;
			};
			const copied = new ArrayBuffer(indexed.byteLength);
			const dest = new Uint8Array(copied);
			for (let i = 0; i < dest.byteLength; i++) {
				dest[i] = indexed[i];
			}
			chunks.push(copied);
			total += copied.byteLength;
		}
	}
	const out = new Uint8Array(total);
	let offset = 0;
	for (const chunk of chunks) {
		const view = new Uint8Array(chunk);
		for (let i = 0; i < view.byteLength; i++) {
			out[offset + i] = view[i];
		}
		offset += view.byteLength;
	}
	return out;
}

/** Message from a failed {@link AnalyticsCardDataRefService.fetchPayload} result. */
export function cardDataRefErrorMessage(result: CardDataRefFetchResult): string {
	if ('error' in result) {
		return result.error.message;
	}
	return 'Card dataRef fetch failed';
}

async function parseCardDataRefBody(buffer: ArrayBuffer): Promise<unknown> {
	const bytes = new Uint8Array(buffer);
	const decoded = isGzipBuffer(bytes) ? await inflateGzip(bytes) : bytes;
	const text = new TextDecoder().decode(copyBytes(decoded));
	return JSON.parse(text) as unknown;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsCardDataRefService {
	private readonly origin = inject(ANALYTICS_CARD_DATA_REF_ORIGIN);

	async fetchPayload(dataRef: string): Promise<CardDataRefFetchResult> {
		let url: string;
		try {
			url = buildCardDataRefUrl(this.origin, dataRef);
			log('[Analytics][dataRef] fetchPayload: url built', {
				origin: this.origin,
				dataRef,
				url,
			});
		} catch (err) {
			const wrapped = err instanceof Error ? err : new Error(String(err));
			error('[Analytics] Invalid card dataRef', wrapped);
			return { ok: false, error: wrapped };
		}

		try {
			log('[Analytics][dataRef] fetchPayload: requesting', { url });
			const response = await fetch(url, { credentials: 'include' });
			if (!response.ok) {
				const wrapped = new Error(
					`Card dataRef fetch failed (${response.status})`
				);
				error('[Analytics] Card dataRef fetch failed', {
					url,
					status: response.status,
				});
				return { ok: false, error: wrapped };
			}
			const buffer = await response.arrayBuffer();
			const data = await parseCardDataRefBody(buffer);
			log('[Analytics][dataRef] fetchPayload: parsed', {
				url,
				byteLength: buffer.byteLength,
				dataKeys:
					data && typeof data === 'object' && !Array.isArray(data)
						? Object.keys(data as Record<string, unknown>)
						: [],
			});
			return { ok: true, data };
		} catch (err) {
			const wrapped = err instanceof Error ? err : new Error(String(err));
			error('[Analytics] Card dataRef fetch/parse failed', wrapped);
			return { ok: false, error: wrapped };
		}
	}
}
